import { TaskColumn } from "@common/types/views/tasksView";
import type { DataGridColumn } from "@design-system/DataGrid";
import type { NestedTaskRow } from "../types";
import { TaskActionsCell } from "./cells/TaskActionsCell";
import { TaskClientCell } from "./cells/TaskClientCell";
import { TaskCompletedByCell } from "./cells/TaskCompletedByCell";
import { TaskCreatedByCell } from "./cells/TaskCreatedByCell";
import { TaskClientsSummary } from "./cells/taskGroupSummaries";
import type { TaskListCell } from "./cells/taskRowCells";
import { TaskServicePeriodCell } from "./cells/TaskServicePeriodCell";
import {
    TaskCompletedAtCell,
    TaskCreatedAtCell,
    TaskUpdatedAtCell,
} from "./cells/TaskTimestampCell";
import { TaskVisibilityCell } from "./cells/TaskVisibilityCell";
import {
    buildDueDateColumn,
    buildEstimateColumn,
    buildGroupColumn,
    buildPriorityColumn,
    buildServiceColumn,
    buildStageColumn,
    buildStatusColumn,
    col,
    NO_VALUE,
    type TaskColumnBuild,
} from "./editableTaskColumns";
import { buildAssigneeColumn, buildEntitiesColumn } from "./peopleTaskColumns";
import type {
    TaskListColumnConfigs,
    TaskListConfig,
} from "./taskListColumnConfigs";
import type { TaskListActions } from "./taskListActions";
import { buildTitleColumn } from "./titleTaskColumn";

type TaskColumnConfig = TaskListColumnConfigs[TaskColumn];

type EditableTaskColumn =
    | TaskColumn.STATUS
    | TaskColumn.PRIORITY
    | TaskColumn.GROUP
    | TaskColumn.CLOSE
    | TaskColumn.CLOSE_STAGE
    | TaskColumn.DUE_DATE
    | TaskColumn.ESTIMATE
    | TaskColumn.ASSIGNEE
    | TaskColumn.ENTITIES;

/** Each list-edited column's builder. */
const EDITABLE_COLUMNS: Record<
    EditableTaskColumn,
    (build: TaskColumnBuild) => DataGridColumn<NestedTaskRow>
> = {
    [TaskColumn.STATUS]: buildStatusColumn,
    [TaskColumn.PRIORITY]: buildPriorityColumn,
    [TaskColumn.GROUP]: buildGroupColumn,
    [TaskColumn.CLOSE]: buildServiceColumn,
    [TaskColumn.CLOSE_STAGE]: buildStageColumn,
    [TaskColumn.DUE_DATE]: buildDueDateColumn,
    [TaskColumn.ESTIMATE]: buildEstimateColumn,
    [TaskColumn.ASSIGNEE]: buildAssigneeColumn,
    [TaskColumn.ENTITIES]: buildEntitiesColumn,
};

/** Each read-only column's cell. */
const READ_ONLY_CELLS: Record<
    Exclude<TaskColumn, EditableTaskColumn | TaskColumn.NAME>,
    TaskListCell
> = {
    [TaskColumn.SERVICE_PERIOD]: TaskServicePeriodCell,
    [TaskColumn.VISIBILITY]: TaskVisibilityCell,
    [TaskColumn.CLIENT]: TaskClientCell,
    [TaskColumn.CREATED_AT]: TaskCreatedAtCell,
    [TaskColumn.CREATED_BY]: TaskCreatedByCell,
    [TaskColumn.UPDATED_AT]: TaskUpdatedAtCell,
    [TaskColumn.COMPLETED_BY]: TaskCompletedByCell,
    [TaskColumn.COMPLETED_AT]: TaskCompletedAtCell,
    [TaskColumn.ACTIONS]: TaskActionsCell,
};

/** What read-only columns show on collapsed group rows. */
const READ_ONLY_SUMMARIES: Partial<Record<TaskColumn, TaskListCell>> = {
    [TaskColumn.CLIENT]: TaskClientsSummary,
};

const isEditableColumn = (id: TaskColumn): id is EditableTaskColumn =>
    id in EDITABLE_COLUMNS;

/**
 * One config column as a DataGrid column. Each starts at its config minimum
 * and shares leftover width equally up to its maximum, pinned or not; a
 * resize fixes only the resized column's width. A column whose minimum and
 * maximum match is fixed, as in ViewTable: it doesn't resize, and its
 * `maxSize` caps a saved width, which stays in the view untouched.
 */
const toColumn = ({
    id,
    config,
    actions,
    isTitleEditable,
}: {
    id: TaskColumn;
    config: TaskColumnConfig;
    actions: TaskListActions;
    isTitleEditable: boolean;
}): DataGridColumn<NestedTaskRow> => {
    if (id === TaskColumn.ACTIONS) {
        return col.actions({
            id,
            render: READ_ONLY_CELLS[id],
            width: config.minSize,
            pin: "end",
        });
    }
    const isFixedWidth = config.minSize === config.maxSize;
    const options = {
        header: config.header,
        ariaLabel: config.name,
        size: config.minSize,
        minSize: config.minSize,
        maxSize: config.maxSize,
        fill: 1,
        fillUntilResized: true,
        enableSorting: Boolean(config.sort),
        enableHiding: !config.immutable,
        enableResizing: !isFixedWidth,
    };
    const columnDef = {
        sortDescFirst: config.sort?.defaultOrder !== "asc",
        ...(isFixedWidth && { maxSize: config.maxSize }),
    };
    if (id === TaskColumn.NAME) {
        return {
            ...buildTitleColumn({ options, actions, isTitleEditable }),
            ...columnDef,
        };
    }
    if (isEditableColumn(id)) {
        return { ...EDITABLE_COLUMNS[id]({ options, actions }), ...columnDef };
    }
    const textColumn = col.text(
        { id, value: NO_VALUE },
        {
            ...options,
            render: READ_ONLY_CELLS[id],
            summary: READ_ONLY_SUMMARIES[id],
        },
    );
    return { ...textColumn, ...columnDef };
};

/**
 * The task list's DataGrid columns, one per config column the page shows
 * (internal ones are left out), in `TaskColumn` order, after a pinned select
 * column when rows can be selected. Memoize on the inputs: new columns
 * remount the cells.
 */
export const buildTaskListColumns = ({
    config,
    canSelectRows,
    isTitleEditable,
    actions,
}: {
    config: TaskListConfig;
    canSelectRows: boolean;
    /** The `taskTitleInlineEdit` flag: titles edit in place. */
    isTitleEditable: boolean;
    actions: TaskListActions;
}): Array<DataGridColumn<NestedTaskRow>> => {
    const valueColumns = Object.values(TaskColumn).flatMap((id) => {
        const columnConfig = config.columns[id];
        return columnConfig.internal
            ? []
            : [
                  toColumn({
                      id,
                      config: columnConfig,
                      actions,
                      isTitleEditable,
                  }),
              ];
    });
    return canSelectRows
        ? [col.select({ pin: "start" }), ...valueColumns]
        : valueColumns;
};
